// src/pages/SlaConfig.jsx
import React, { useEffect, useState, useContext } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

const CARD  = { background: 'var(--c-card)', borderRadius: 12, border: '1px solid var(--c-border)', boxShadow: '0 1px 4px rgba(0,0,0,.06)' };
const LABEL = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--c-text-4)', margin: 0 };

const PRIORITIES = [
    { key: 'low',    label: 'Baja',    color: '#059669' },
    { key: 'medium', label: 'Media',   color: '#2563eb' },
    { key: 'high',   label: 'Alta',    color: '#d97706' },
    { key: 'urgent', label: 'Urgente', color: '#dc2626' },
];

const DEFAULT_SLA = { low: 72, medium: 24, high: 8, urgent: 2 };

function HoursInput({ value, onChange }) {
    return (
        <input
            type="number"
            min={1}
            value={value}
            onChange={e => onChange(Number(e.target.value))}
            style={{
                width: 72, textAlign: 'center', padding: '6px 8px',
                border: '1px solid var(--c-border)', borderRadius: 8,
                background: 'var(--c-input-bg)', color: 'var(--c-text-1)',
                fontSize: 14, fontWeight: 600,
            }}
        />
    );
}

export default function SlaConfig() {
    const { user } = useContext(AuthContext);
    const [depts,   setDepts]   = useState([]);
    const [slas,    setSlas]    = useState({});   // { [deptId]: { low, medium, high, urgent } }
    const [saving,  setSaving]  = useState({});   // { [deptId]: bool }
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/departments')
            .then(res => {
                let data = res.data;
                // Líderes, agentes líderes y agentes autorizadores solo ven sus departamentos
                if ((user?.role === 'leader' || (user?.role === 'agent' && (user?.isLeader || user?.canAuthorize))) && user?.departments?.length) {
                    const assignedIds = user.departments.map(d => String(d._id || d));
                    data = data.filter(d => assignedIds.includes(String(d._id)));
                }
                setDepts(data);
                const map = {};
                data.forEach(d => {
                    map[d._id] = { ...DEFAULT_SLA, ...(d.sla || {}) };
                });
                setSlas(map);
            })
            .catch(() => toast.error('Error al cargar departamentos'))
            .finally(() => setLoading(false));
    }, [user]);

    const handleChange = (deptId, priority, value) => {
        setSlas(prev => ({
            ...prev,
            [deptId]: { ...prev[deptId], [priority]: value }
        }));
    };

    const handleSave = async (dept) => {
        setSaving(prev => ({ ...prev, [dept._id]: true }));
        try {
            await api.patch(`/departments/${dept._id}`, {
                name:        dept.name,
                description: dept.description,
                sla:         slas[dept._id],
            });
            toast.success(`SLA de "${dept.name}" guardado`);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al guardar');
        } finally {
            setSaving(prev => ({ ...prev, [dept._id]: false }));
        }
    };

    const handleResetRow = (deptId) => {
        setSlas(prev => ({ ...prev, [deptId]: { ...DEFAULT_SLA } }));
    };

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>
            <h4 style={{ fontWeight: 700, margin: '0 0 4px', color: 'var(--c-text-1)' }}>Configuración de SLA</h4>
            <p style={{ color: 'var(--c-text-3)', fontSize: 14, margin: '0 0 24px' }}>
                Define el tiempo máximo de resolución en horas por prioridad para cada departamento.
            </p>

            {/* Defaults de referencia */}
            <div style={{ ...CARD, padding: '16px 20px', marginBottom: 24, borderLeft: '4px solid #6b7280' }}>
                <p style={{ ...LABEL, marginBottom: 10 }}>Valores por defecto del sistema</p>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {PRIORITIES.map(p => (
                        <div key={p.key} style={{ textAlign: 'center' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: p.color, display: 'block', marginBottom: 2 }}>
                                {p.label}
                            </span>
                            <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--c-text-1)' }}>
                                {DEFAULT_SLA[p.key]}h
                            </span>
                        </div>
                    ))}
                </div>
                <p style={{ fontSize: 11, color: 'var(--c-text-4)', margin: '10px 0 0' }}>
                    Los departamentos sin SLA personalizado usan estos valores.
                </p>
            </div>

            {/* Tabla de SLA por departamento */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <Spinner animation="border" />
                </div>
            ) : depts.length === 0 ? (
                <p style={{ color: 'var(--c-text-4)', textAlign: 'center' }}>No hay departamentos creados.</p>
            ) : (
                <>
                    {/* Encabezado de columnas */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr repeat(4, 88px) 160px',
                        gap: 8,
                        padding: '8px 20px',
                        alignItems: 'center',
                    }}>
                        <span style={LABEL}>Departamento</span>
                        {PRIORITIES.map(p => (
                            <span key={p.key} style={{ ...LABEL, color: p.color, textAlign: 'center' }}>
                                {p.label}
                            </span>
                        ))}
                        <span />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {depts.map(dept => (
                            <div
                                key={dept._id}
                                className="card-hover"
                                style={{
                                    ...CARD,
                                    display: 'grid',
                                    gridTemplateColumns: '1fr repeat(4, 88px) 160px',
                                    gap: 8,
                                    padding: '14px 20px',
                                    alignItems: 'center',
                                }}
                            >
                                <div>
                                    <p style={{ fontWeight: 700, color: 'var(--c-text-1)', margin: 0, fontSize: 14 }}>
                                        {dept.name}
                                    </p>
                                    {dept.description && (
                                        <p style={{ color: 'var(--c-text-4)', fontSize: 12, margin: 0 }}>
                                            {dept.description}
                                        </p>
                                    )}
                                </div>

                                {dept.slaMode === 'priority' ? (
                                    <>
                                        {PRIORITIES.map(p => (
                                            <div key={p.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                <HoursInput
                                                    value={slas[dept._id]?.[p.key] ?? DEFAULT_SLA[p.key]}
                                                    onChange={v => handleChange(dept._id, p.key, v)}
                                                />
                                                <span style={{ fontSize: 10, color: 'var(--c-text-4)' }}>horas</span>
                                            </div>
                                        ))}
                                    </>
                                ) : dept.slaMode === 'deliveryDate' ? (
                                    <div style={{ gridColumn: 'span 5', textAlign: 'center', color: 'var(--c-text-3)', fontSize: 12, padding: '8px 4px' }}>
                                        📅 Este departamento usa <strong>fecha de entrega</strong> definida por el solicitante. El SLA no depende de la prioridad.
                                    </div>
                                ) : dept.slaMode === 'materialType' ? (
                                    <div style={{ gridColumn: 'span 5', textAlign: 'center', color: 'var(--c-text-3)', fontSize: 12, padding: '8px 4px' }}>
                                        🎨 Este departamento usa <strong>tipo de material</strong> (horas × cantidad) y fecha de entrega comprometida.
                                        Las horas por tipo se configuran desde <em>Departamentos → Editar</em>.
                                    </div>
                                ) : (
                                    <div style={{ gridColumn: 'span 5', textAlign: 'center', color: 'var(--c-text-4)', fontSize: 12, padding: '8px 4px' }}>
                                        Modo de SLA no reconocido — no aplica configuración por prioridad.
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                    {dept.slaMode === 'priority' && (
                                        <>
                                            <Button
                                                size="sm" variant="outline-secondary"
                                                onClick={() => handleResetRow(dept._id)}
                                                style={{ borderRadius: 8, fontSize: 11 }}
                                                title="Restaurar valores por defecto"
                                            >
                                                Reset
                                            </Button>
                                            <Button
                                                size="sm" variant="primary"
                                                onClick={() => handleSave(dept)}
                                                disabled={saving[dept._id]}
                                                style={{ borderRadius: 8, fontSize: 12 }}
                                            >
                                                {saving[dept._id]
                                                    ? <Spinner size="sm" animation="border" />
                                                    : 'Guardar'}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <p style={{ color: 'var(--c-text-4)', fontSize: 12, marginTop: 20 }}>
                Los cambios aplican solo a tickets nuevos. Los tickets existentes conservan su vencimiento original.
            </p>
        </div>
    );
}
